import { endOfYear, startOfYear } from 'date-fns';
import {
  defaultTo,
  has,
  isEmpty,
  isNil,
  mapValues,
  path,
  pathOr,
  pluck,
  propOr,
  uniq,
} from '../../utils/fp';
import { OVERVIEW, TABLE, CHART_IDS, MICRODATA } from '../../utils/constants';
import { dateWithoutTZ, getDateInTheRange } from '../../utils/date';
import { DEFAULT_SORT_INDEX_SELECTED, defaultSortItems } from '../search/constants';

// R.intersection: unique elements present in both lists.
const intersection = (a, b) => uniq(a.filter(x => b.includes(x)));

const viewerIds = {
  ...CHART_IDS,
  TABLE,
  OVERVIEW,
  MICRODATA,
};

const app = propOr({}, 'app', window.SETTINGS);
export const defaultViewer = defaultTo(
  OVERVIEW,
  viewerIds[propOr('', 'defaultView', app).toUpperCase()],
);
// settings.json app.chipsCollapseThreshold: how many applied-filter chips a
// dimension shows individually before collapsing them into a single
// "<n> items | + Show more" chip (see DE issue #1787).
export const chipsCollapseThreshold = propOr(6, 'chipsCollapseThreshold', app);
export const hasFooterCopyright = pathOr(true, ['footer', 'copyright'], app);
export const hasFooterSourceName = pathOr(true, ['footer', 'sourceName'], app);
export const hasContactForm = pathOr(true, ['contact', 'form'], app);
export const hasContactCaptcha =
  pathOr(true, ['contact', 'captcha'], app) && window.CONFIG?.hasCaptcha;
export const isAllContact = pathOr(true, ['contact', 'TitleFullnameOrganisation'], app);
export const i18n = propOr({}, 'i18n', window.SETTINGS);
export const search = propOr({}, 'search', window.SETTINGS);
export const theme = propOr({}, 'theme', window.SETTINGS);
const sdmx = propOr({}, 'sdmx', window.SETTINGS);
export const viewer = propOr({}, 'viewer', window.SETTINGS);
export const analytics = propOr({}, 'analytics', window.SETTINGS);
const contact = propOr({}, 'contact', window.SETTINGS);
export const sanitizeOptions = propOr({}, 'htmlSanitization', window.SETTINGS);

const chart = propOr({}, 'chart', window.SETTINGS);
export const chartIsVisible = propOr(true, 'isVisible', chart);

const share = propOr({}, 'share', window.SETTINGS);
// share is disabled by blanking the endpoint or removing it entirely; short url
// generation goes down with it, so it can't be reached by authenticated users.
export const hasShareEndpoint = !isEmpty(propOr('', 'endpoint', share));

export const outerPalette = propOr({}, 'outerPalette', theme);

export const defaultSearchRows = search.defaultRows;
export const homeFacetLevel2Clickable = search.homeFacetLevel2Clickable;
export const isHomeFacetCentered = search.homeFacetCentered;
export const homeFacetIds = propOr([], 'homeFacetIds', search);
export const setOfHideHomeFacetItemIDs = new Set(propOr([], 'hideHomeFacetItemIDs', search));
export const setOfHideHomeAndResultFacetItemIDs = new Set(
  propOr([], 'hideHomeAndResultFacetItemIDs', search),
);
export const downloadableDataflowResults = propOr(false, 'downloadableDataflowResults', search);
const selectedFacetIds = settings =>
  intersection(propOr([], 'homeFacetIds', settings), propOr([], 'expandedHomeFacets', settings));
export const selectedFacetId = list => intersection(selectedFacetIds(search), pluck('id', list))[0];
export const defaultFacetsNumber = propOr(6, 'defaultFacetsNumber', search);
export const defaultRelatedDataflowsNumber = propOr(
  4,
  'defaultRelatedDataflowsNumber',
  propOr({}, 'overview', window.SETTINGS),
);

export const hasNoSearch = !has('endpoint', search);

export const displayChildren = propOr(false, 'spotlightDisplayChildren', search);

export const sortOptionsOrdered = propOr(defaultSortItems, 'sortOptionsOrdered', search);
export const sortOptionIndex = propOr(
  DEFAULT_SORT_INDEX_SELECTED,
  'defaultSortOptionIndex',
  search,
);

const getLocalisedSettings = (id, locale = 'en', propName) => {
  const settings = propOr({}, propName, window.SETTINGS);
  const setting = settings[id];
  if (!setting) return;
  if (typeof setting === 'string') return setting;
  return propOr('', locale, setting);
};

export const getAsset = (id, locale = 'en') => getLocalisedSettings(id, locale, 'assets');
export const getApp = (id, locale = 'en') => getLocalisedSettings(id, locale, 'app');

export const locales = propOr({}, 'locales', i18n);
export const getLocaleId = id => (has(id, locales) ? id : i18n.localeId);

const scope = window.CONFIG?.member?.scope;
const spaces = propOr({}, 'spaces', scope);
export const datasources = propOr({}, 'datasources', scope);
export const getDatasource = id => defaultTo(spaces[id], datasources[id]);

export const getSpaceFromUrl = url => Object.values(spaces).find(space => space.url === url);
export const getSpaceFromDatasourceId = id => {
  const datasource = datasources[id];
  return getSpaceFromUrl(datasource?.url);
};
const getSpace = id => (has(id, spaces) ? spaces[id] : getSpaceFromDatasourceId(id));

export const getDatasourceIds = id => {
  // we want datasource id(s) from an id
  // the id can be a datasource id or a space id
  // depending on coming from DE search or DLM eye
  if (has(id, datasources)) return [id];
  if (!has(id, spaces)) return [];
  return Object.entries(datasources)
    .filter(([, { id: spaceId }]) => spaceId === id)
    .map(([key]) => key);
};

export const defaultFrequency = sdmx.frequency;
export const sdmxPeriodBoundaries = (() => {
  const [start, end] = pathOr([], ['period', 'boundaries'], sdmx);
  const startDate = isNil(start) || isEmpty(start) ? Date.now() : new Date(start);
  const endDate = isNil(end) || isEmpty(end) ? Date.now() : new Date(end);
  return [startOfYear(startDate), endOfYear(endDate)];
})();
export const sdmxPeriod = (() => {
  const [start, end] = pathOr([], ['period', 'default'], sdmx);
  const startDate = isNil(start) || isEmpty(start) ? undefined : dateWithoutTZ(new Date(start));
  const endDate = isNil(end) || isEmpty(end) ? undefined : dateWithoutTZ(new Date(end));
  return [startDate, endDate].map(getDateInTheRange(sdmxPeriodBoundaries));
})();

export const isDefaultTimeDimensionInverted =
  pathOr([], ['period', 'defaultSort'], sdmx) === 'desc';

export const defaultLastNPeriods = pathOr(5, ['period', 'lastNPeriods'], sdmx);

export const valueIcons = sdmx.valueIcons;

export const chartOptions = chart.options;

export const mapOptions = Object.values(path(['chart', 'maps'], window.SETTINGS) || {}).flatMap(
  ({ id, levels }) =>
    levels.map(level => ({
      ...level,
      id: `${id}:${level.id}`,
      mapId: id,
      levelId: level.id,
    })),
);
export const getMapName =
  ({ localeId }) =>
  ({ id, names }) =>
    propOr(id, localeId, names);

export const getMap = mapId => path(['chart', 'maps', mapId], window.SETTINGS);

export const dataRequestSize = (() => {
  const requested = path(['sdmx', 'dataRequestSize'], window.SETTINGS);
  // rollback for retro compatibility
  const withFallback = defaultTo(path(['table', 'cellsLimit'], window.SETTINGS), requested);
  return typeof withFallback !== 'number' ? 500 : withFallback;
})();

export const isPaginationActivated = pathOr(
  false,
  ['sdmx', 'isPaginationActivated'],
  window.SETTINGS,
);

export const customAttributes = propOr({}, 'attributes', sdmx);

export const isSpaceExternal = spaceId => {
  const space = getSpace(spaceId);
  return isNil(space) ? true : propOr(false, 'isExternal', space);
};

export const hasExternalAuth = spaceId => {
  const space = getSpace(spaceId);
  return isNil(space) ? true : propOr(false, 'hasExternalAuth', space);
};

export const getSpaceRangeSizeLimit = id => propOr(0, 'rangeSizeLimit', getSpace(id));

export const isAuthRequired = window.CONFIG?.member?.scope?.oidc?.required;

export const getContactMailTo = key => path(['mailTo', key], contact);

export const defaultCombinations = pathOr({}, ['sdmx', 'defaultCombinations'], window.SETTINGS);

// The combinations of a settings definition, named by position: the vis page parses
// the tenant's current settings, a latest share the ones it was shared with.
export const parseDefaultCombinations = (definition, locale) => {
  if (isNil(definition) || isEmpty(definition)) {
    return [];
  }
  const { concepts, names } = definition;
  const titles = concepts.split(';');
  const texts = mapValues(text => text.split(';'), names);
  return titles.map((title, index) => {
    const split = title.split(':');
    // NB: the original guard `R.length(split !== 2)` evaluated to NaN (falsy),
    // so it never returned null — dead code, dropped.
    const [id, codes] = split;
    return {
      id,
      concepts: codes.split(','),
      name: has(locale, texts) ? texts[locale][index].split(':').at(-1) : `[${id}]`,
    };
  });
};

export const getDefaultCombinations = locale =>
  parseDefaultCombinations(defaultCombinations, locale);

export const facets = propOr({}, 'facets', window.SFSSETTINGS);
export const timePeriodFacetId = propOr({}, 'timePeriod', facets);
export const getFacetDimensionId = localeId => facets?.dimensions?.[localeId];

export const tableCellsLimit = pathOr(0, ['table', 'cellsLimit'], window.SETTINGS);

export const getDelimiters = (locale = 'en') => {
  return locales?.[locale]?.delimiters || {};
};
